import React, { useEffect, useMemo, useRef, useState } from "react";
import { animate, stagger } from "animejs";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  BrowserRouter,
  Link,
  NavLink,
  Outlet,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import {
  CalendarDays,
  Baby,
  Check,
  ChevronLeft,
  Clock,
  Compass,
  ExternalLink,
  Gift,
  Hammer,
  MapPin,
  Music2,
  Menu,
  Plus,
  Ticket,
  Trash2,
  Sun,
  Sunrise,
  X,
  Utensils,
  Users,
} from "lucide-react";
import parakeetBlink from "./assets/mascots/parakeet-blink.png";
import ListingImage from "./components/ListingImage";
import AutoRide from "./ride/AutoRide";
import "./App.css";

const CATEGORIES = [
  "EVENT",
  "FOOD_DRINK",
  "KIDS",
  "MUSIC",
  "WORKSHOP",
  "SPORTS",
  "COMMUNITY",
  "MARKET",
  "NEW_OPENING",
  "EXPERIENCE",
  "OTHER",
];
const labels = {
  EVENT: "Event",
  FOOD_DRINK: "Food & drink",
  KIDS: "Kids",
  MUSIC: "Music",
  WORKSHOP: "Workshop",
  SPORTS: "Sports",
  COMMUNITY: "Community",
  MARKET: "Market",
  NEW_OPENING: "New opening",
  EXPERIENCE: "Experience",
  OTHER: "Other",
};
const pad = (n) => String(n).padStart(2, "0");
const dateFromNow = (days) => {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const next = (day) => (day - new Date().getDay() + 7) % 7 || 7;
const now = () => new Date().toISOString();
const sample = (
  id,
  title,
  description,
  category,
  days,
  time,
  venue,
  locality,
  price,
  free,
  featured = false,
  editorialNote = "",
) => ({
  id,
  title,
  description,
  category,
  start_date: dateFromNow(days),
  end_date: dateFromNow(days),
  start_time: time,
  venue,
  locality,
  latitude: null,
  longitude: null,
  price_text: price,
  is_free: free,
  source_name: "Demo listing",
  source_url: "https://example.com",
  booking_url: free ? "" : "https://example.com",
  image_url: "",
  verified_at: now(),
  created_at: now(),
  featured,
  status: "UPCOMING",
  is_demo: true,
  editorial_note: editorialNote,
});
const SAMPLES = [
  sample(
    "pottery-social",
    "Sunday Pottery Social",
    "A relaxed beginner-friendly hand-building session with clay, glazing and firing included.",
    "WORKSHOP",
    next(0),
    "11:00",
    "Kaolin Studio",
    "Whitefield",
    "Rs 799",
    false,
    true,
    "Genuinely beginner-friendly; no one will judge your bowl. Wear something you can get clay on.",
  ),
  sample(
    "lake-cleanup",
    "Community Lake Clean-up",
    "A volunteer morning to clean the lake edge and meet neighbours. Gloves and bags provided.",
    "COMMUNITY",
    next(6),
    "07:30",
    "Varthur Lake Trail",
    "Varthur",
    "Free",
    true,
    "A quiet way to spend a Sunday morning. Go early if you want the lake edge to feel calm.",
  ),
  sample(
    "jazz-brunch",
    "Courtyard Jazz Brunch",
    "For when Sunday brunch needs something better than another Spotify playlist.",
    "FOOD_DRINK",
    next(0),
    "12:30",
    "The Courtyard",
    "ITPL Main Road",
    "From Rs 1,200",
    false,
    false,
    "The trio is the reason to stay past brunch. Outdoor tables fill up quickly after 11.",
  ),
  sample(
    "science-lab",
    "Little Inventors Science Lab",
    "Hands-on experiments for ages 7-11, from mini rockets to colourful chemistry.",
    "KIDS",
    1,
    "16:30",
    "Active Arena",
    "Kundalahalli",
    "Rs 450",
    false,
    false,
    "A good first workshop if you want to make something with your hands and leave your phone in your bag.",
  ),
  sample(
    "indie-evening",
    "Indie Under the Trees",
    "An intimate evening of acoustic originals by Bengaluru singer-songwriters.",
    "MUSIC",
    next(6),
    "18:30",
    "Inner Circle Park",
    "Whitefield",
    "Free",
    true,
    false,
    "Bring a friend who says they do not like live music. The trees and low volume usually win them over.",
  ),
  sample(
    "sunday-market",
    "Whitefield Sunday Market",
    "Fresh produce, sourdough, condiments, plants and handmade goods.",
    "MARKET",
    next(0),
    "09:00",
    "Hagadur Community Hall",
    "Hagadur",
    "Free entry",
    true,
  ),
  sample(
    "new-bakery",
    "New: Slow Ferment Bakehouse",
    "A new neighbourhood bakery serving sourdough, pastries and filter coffee.",
    "NEW_OPENING",
    3,
    "08:00",
    "Slow Ferment Bakehouse",
    "Seegehalli",
    "A la carte",
    false,
    true,
  ),
];
const DATA_KEY = "anvesho_happenings_v1",
  SUBMIT_KEY = "anvesho_submissions_v1";
const read = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || fallback;
  } catch {
    return fallback;
  }
};
const track = (event, properties = {}) => {
  window.dispatchEvent(
    new CustomEvent("anvesho:analytics", {
      detail: { event, properties, at: now() },
    }),
  );
};
const prettyDate = (value) =>
  new Intl.DateTimeFormat("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date(`${value}T12:00:00`));
const verified = (value) =>
  new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));

export default function App() {
  const [items, setItems] = useState(() => {
    const stored = read(DATA_KEY, []);
    const realItems = stored.filter((item) => !item.is_demo);
    return realItems.length ? [...realItems, ...SAMPLES] : SAMPLES;
  });
  useEffect(
    () => localStorage.setItem(DATA_KEY, JSON.stringify(items)),
    [items],
  );
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<AutoRide />} />
        <Route element={<Layout />}>
          <Route path="issue" element={<Home items={items} />} />
          <Route path="weekend" element={<Weekend items={items} />} />
          <Route
            path="new"
            element={
              <Listing
                title="New in Whitefield"
                intro="Fresh cafes, restaurants, stores and spaces worth knowing about."
                items={items.filter((x) => x.category === "NEW_OPENING")}
              />
            }
          />
          <Route
            path="category/:category"
            element={<Category items={items} />}
          />
          <Route path="happening/:id" element={<Detail items={items} />} />
          <Route path="submit" element={<Submit />} />
          <Route
            path="admin"
            element={<Admin items={items} setItems={setItems} />}
          />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

function Layout() {
  const [open, setOpen] = useState(false);
  const issueDate = new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());
  return (
    <div className="site-shell">
      <div className="demo-bar">
        <span>Whitefield's independent guide to what is worth your time</span>
        <b>Sample issue</b>
      </div>
      <header className="masthead">
        <div className="masthead-meta">
          <span>Whitefield, Bengaluru</span>
          <span>{issueDate}</span>
          <span>Issue 01</span>
        </div>
        <Link className="brand" to="/" aria-label="Anvesho home">
          <span className="brand-name">Anvesho</span>
        </Link>
        <div className="masthead-deck">
          <span>The Whitefield Chronicle</span>
          <span>Rituals · Finds · People · Places</span>
          <span>Free of noise</span>
        </div>
        <button
          className="icon menu"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
        <nav className={open ? "open" : ""} aria-label="Main navigation">
          <NavLink to="/" onClick={() => setOpen(false)}>
            This week's issue
          </NavLink>
          <NavLink to="/weekend" onClick={() => setOpen(false)}>
            This weekend
          </NavLink>
          <NavLink to="/new" onClick={() => setOpen(false)}>
            New in Whitefield
          </NavLink>
          <NavLink to="/submit" onClick={() => setOpen(false)}>
            Submit a happening
          </NavLink>
        </nav>
      </header>
      <main>
        <Outlet />
      </main>
      <footer>
        <div>
          <strong>Anvesho</strong>
          <p>The neighbourhood field guide for Whitefield.</p>
        </div>
        <div>
          <Link to="/submit">Submit</Link>
          <Link to="/admin">Local admin</Link>
        </div>
      </footer>
    </div>
  );
}

const timeFilters = ["Today", "Tomorrow", "This Weekend", "This Week"];
const categoryFilters = [
  "Free",
  "Kids",
  "Food & Drink",
  "Music",
  "Workshops",
  "Experiences",
  "Community",
];
function filterItems(items, filter) {
  const today = dateFromNow(0),
    tomorrow = dateFromNow(1),
    week = dateFromNow(7),
    sat = dateFromNow(next(6)),
    sun = dateFromNow(next(0));
  if (filter === "Today") return items.filter((x) => x.start_date === today);
  if (filter === "Tomorrow")
    return items.filter((x) => x.start_date === tomorrow);
  if (filter === "This Weekend")
    return items.filter((x) => [sat, sun].includes(x.start_date));
  if (filter === "This Week")
    return items.filter((x) => x.start_date >= today && x.start_date <= week);
  if (filter === "Free") return items.filter((x) => x.is_free);
  const cat = {
    Kids: "KIDS",
    "Food & Drink": "FOOD_DRINK",
    Music: "MUSIC",
    Workshops: "WORKSHOP",
    Experiences: "EXPERIENCE",
    Community: "COMMUNITY",
  }[filter];
  return cat ? items.filter((x) => x.category === cat) : items;
}

const filterIcons = {
  Today: Sun,
  Tomorrow: Sunrise,
  "This Weekend": CalendarDays,
  "This Week": CalendarDays,
  Free: Gift,
  Kids: Baby,
  "Food & Drink": Utensils,
  Music: Music2,
  Workshops: Hammer,
  Experiences: Compass,
  Community: Users,
};

function WeekSignal({ items, selectedDate, onSelect }) {
  const signalRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const days = Array.from({ length: 7 }, (_, index) => {
    const date = new Date();
    date.setDate(date.getDate() + index);
    return {
      date,
      count: items.filter((item) => item.start_date === dateFromNow(index))
        .length,
    };
  }).filter(({ count }) => count > 0);

  useEffect(() => {
    if (reduceMotion || !signalRef.current) return undefined;
    const dayItems = signalRef.current.querySelectorAll(".week-rail > button");
    const entrance = animate(dayItems, {
      y: [8, 0],
      opacity: [0, 1],
      duration: 420,
      delay: stagger(45),
      ease: "outQuad",
    });
    return () => entrance.cancel();
  }, [reduceMotion]);

  return (
    <motion.div
      className="week-signal"
      ref={signalRef}
      initial={false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.08 }}
    >
      <div className="signal-topline">
        <span>Browse by date</span>
        <b>Next 7 days</b>
      </div>
      <div className="week-rail">
        {days.map(({ date, count }, index) => (
          <button
            type="button"
            className={[
              index === 0 ? "today" : "",
              selectedDate === dateFromNow(index) ? "selected" : "",
            ].join(" ")}
            key={date.toISOString()}
            onClick={() => onSelect(dateFromNow(index))}
            aria-label={`Show happenings on ${prettyDate(dateFromNow(index))}`}
          >
            <span>
              {new Intl.DateTimeFormat("en-IN", { weekday: "short" }).format(
                date,
              )}
            </span>
            <strong>{date.getDate()}</strong>
            <small>{count ? `${count} listed` : "None"}</small>
          </button>
        ))}
      </div>
    </motion.div>
  );
}

function Home({ items }) {
  const [timeActive, setTimeActive] = useState("This Week");
  const [categoryActive, setCategoryActive] = useState("All categories");
  const [selectedDate, setSelectedDate] = useState("");
  const triggerFilter = (filter) => {
    const sessionKey = "anvesho-filter-mascot-seen";
    const first = !sessionStorage.getItem(sessionKey);
    sessionStorage.setItem(sessionKey, "1");
    window.dispatchEvent(new CustomEvent("anvesho:filter-change", { detail: { first, filter } }));
  };
  const visible = useMemo(
    () =>
      (() => {
        const matching = items.filter((x) => x.status !== "CANCELLED");
        const byTime = selectedDate
          ? matching.filter((item) => item.start_date === selectedDate)
          : filterItems(matching, timeActive);
        if (categoryActive === "All categories") return byTime;
        return filterItems(byTime, categoryActive);
      })(),
    [items, timeActive, categoryActive, selectedDate],
  );
  const featured = visible.find((item) => item.featured) || visible[0];
  const feed = visible.filter((item) => item.id !== featured?.id);
  const quickPicks = visible.slice(0, 3);
  return (
    <>
      <section className="hero">
        <motion.div
          className="hero-copy"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="eyebrow">Anvesho's weekly signal</p>
          <h1>Choose your sensory frequency window</h1>
          <p>A local edit of what deserves a place in your week, with notes you will not find on a booking app.</p>
          <div className="hero-proof">
            <span>
              <b>{items.length}</b> things worth leaving home for
            </span>
            <span>
              <b>{items.filter((item) => item.is_free).length}</b> free this
              week
            </span>
          </div>
        </motion.div>
        <div className="frequency-window" aria-label="This week's quick picks">
          <div className="frequency-scale"><span>Quiet</span><i /><span>Full signal</span></div>
          <div className="frequency-picks">
            {quickPicks.map((item, index) => (
              <Link to={`/happening/${item.id}`} className="frequency-pick" key={item.id}>
                <small>0{index + 1} · {labels[item.category]}</small>
                <strong>{item.title}</strong>
                <span>{prettyDate(item.start_date)} · {item.locality}</span>
                <ExternalLink size={14} />
              </Link>
            ))}
          </div>
        </div>
      </section>
      <div className={`editorial-filters accent-${categoryActive.toLowerCase().replaceAll(" ", "-").replace("&", "")}`}>
        <WeekSignal
          items={items}
          selectedDate={selectedDate}
          onSelect={(date) => {
            setSelectedDate(date);
            triggerFilter("date");
            track("filter_used", { filter: "date", date });
          }}
        />
        <div className="filter-group">
          <span>When</span>
          <div>
            {timeFilters.map((filter) => (
              <button
                key={filter}
                className={
                  timeActive === filter && !selectedDate ? "active" : ""
                }
                onClick={() => {
                  setTimeActive(filter);
                  setSelectedDate("");
                  triggerFilter(filter);
                  track("filter_used", { filter });
                }}
              >
                {filterIcons[filter] ? React.createElement(filterIcons[filter], { size: 14, strokeWidth: 1.8 }) : null}
                <span>{filter}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="filter-group">
          <span>Explore</span>
          <div>
            {["All categories", ...categoryFilters].map((filter) => (
              <button
                key={filter}
                className={categoryActive === filter ? "active" : ""}
                onClick={(event) => {
                  setCategoryActive(filter);
                  triggerFilter(filter, event.currentTarget);
                  track("filter_used", { filter });
                }}
              >
                {filter === "All categories" ? <Compass size={14} strokeWidth={1.8} /> : null}
                {filterIcons[filter] ? React.createElement(filterIcons[filter], { size: 14, strokeWidth: 1.8 }) : null}
                <span>{filter}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      {/*
        <div className="filter-buttons">
          {filters.map((f) => (
            <button
              key={f}
              className={active === f ? "active" : ""}
              onClick={() => {
                setActive(f);
                setSelectedDate("");
                track("filter_used", { filter: f });
              }}
            >
              {f}
            </button>
          ))}
        </div>
      */}
      <motion.section
        className="content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.45 }}
      >
        <div className="section-title">
          <div>
            <p className="eyebrow">The week's field report</p>
            <h2>
              {selectedDate
                ? prettyDate(selectedDate)
                : categoryActive === "All categories"
                  ? timeActive === "This Week"
                    ? `The ${visible.length} curated happenings`
                    : timeActive
                  : categoryActive}
            </h2>
          </div>
          <span>{visible.length} happenings</span>
        </div>
        <AnimatePresence mode="popLayout">
          {visible.length ? (
            <div className="editorial-feed">
              {featured && <FeaturedStory item={featured} />}
              <div className="feed-grid">
                {feed.map((item, index) => (
                  <EditorialCard key={item.id} item={item} index={index} />
                ))}
              </div>
            </div>
          ) : (
            <Empty />
          )}
        </AnimatePresence>
      </motion.section>
    </>
  );
}
function FeaturedStory({ item }) {
  const featuredImage = listingImage(item);
  return (
    <motion.article
      className="featured-story"
      layout
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Link className="featured-photo" to={`/happening/${item.id}`} aria-label={`View happening: ${item.title}`}>
        <ListingImage key={featuredImage} src={featuredImage} alt={item.title} position={listingImagePosition(item)} eager />
        <span>Anvesho pick</span>
      </Link>
      <div className="featured-copy">
        <div className="feature-stamps"><span>Curated in Whitefield</span><span>{item.locality || "Whitefield"}</span></div>
        <p className="eyebrow">Worth leaving home for</p>
        <h3>
          <Link to={`/happening/${item.id}`}>{item.title}</Link>
        </h3>
        <p>{item.description}</p>
        {item.editorial_note && <p className="editorial-note">{item.editorial_note}</p>}
        <div className="story-meta">
          <span>
            {prettyDate(item.start_date)} · {item.start_time}
          </span>
          <span>
            {item.venue}, {item.locality}
          </span>
          <span className="distance-stamp"><MapPin size={13} /> {item.locality || "Whitefield"}</span>
          <strong>{item.price_text}</strong>
        </div>
        <Link className="story-link" to={`/happening/${item.id}`}>
          View happening →
        </Link>
      </div>
    </motion.article>
  );
}

function listingImage(item) {
  if (item.image_url) return item.image_url;
  return "";
}

function listingImagePosition(item) {
  return {
    KIDS: "76% center",
    MUSIC: "35% center",
    WORKSHOP: "8% center",
    FOOD_DRINK: "72% center",
  }[item.category] || "center";
}

function EditorialCard({ item, index }) {
  const editorialImage = item.image_url || "";
  return (
    <motion.article
      className={`editorial-card editorial-card-${index % 3} category-${item.category.toLowerCase()}${editorialImage ? " editorial-card-photo" : ""}`}
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.16) }}
    >
      {editorialImage && (
        <Link className="editorial-photo" to={`/happening/${item.id}`} aria-label={`View happening: ${item.title}`}>
          <ListingImage key={editorialImage} src={editorialImage} alt={item.title} position={listingImagePosition(item)} />
        </Link>
      )}
      <div>
        <div className="card-kicker"><p className="eyebrow">{labels[item.category]}</p><span>{item.locality || "Whitefield"}</span></div>
        <h3>
          <Link to={`/happening/${item.id}`}>{item.title}</Link>
        </h3>
        <p>{item.description}</p>
        {item.editorial_note && <p className="editorial-note">{item.editorial_note}</p>}
        <span className="editorial-meta"><MapPin size={13} />
          {prettyDate(item.start_date)} · {item.venue}
        </span>
        <div className="editorial-action"><strong>{item.price_text || "Price not listed"}</strong><Link to={`/happening/${item.id}`}>View happening →</Link></div>
      </div>
    </motion.article>
  );
}

function Card({ item, index = 0 }) {
  useEffect(() => track("listing_view", { id: item.id }), [item.id]);
  return (
    <motion.article
      className="card"
      layout
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: Math.min(index * 0.045, 0.2) }}
    >
      <Link className="collection-image" to={`/happening/${item.id}`} aria-label={`View happening: ${item.title}`}><ListingImage src={listingImage(item)} alt={item.title} position={listingImagePosition(item)} /></Link>
      <div className="card-body">
        <div className="meta">
          <span>
            <CalendarDays />
            {prettyDate(item.start_date)}
          </span>
          <span>
            <Clock />
            {item.start_time}
          </span>
        </div>
        <h3>
          <Link
            to={`/happening/${item.id}`}
            onClick={() => track("listing_click", { id: item.id })}
          >
            {item.title}
          </Link>
        </h3>
        <p>{item.description}</p>
        <div className="venue">
          <MapPin />
          <span>
            <strong>{item.venue}</strong>
            {item.locality}
          </span>
        </div>
        <div className="card-foot">
          <strong>{item.price_text || "Price not listed"}</strong>
          <Link to={`/happening/${item.id}`}>View happening &rarr;</Link>
        </div>
      </div>
    </motion.article>
  );
}
function Listing({ title, intro, items }) {
  return (
    <section className="page">
      <Back />
      <div className="page-title">
        <h1>{title}</h1>
        <p>{intro}</p>
      </div>
      {items.length ? (
        <div className="grid">
          {items.map((x) => (
            <Card key={x.id} item={x} />
          ))}
        </div>
      ) : (
        <Empty />
      )}
    </section>
  );
}
function Category({ items }) {
  const { category } = useParams(),
    key = category.toUpperCase().replaceAll("-", "_");
  return (
    <Listing
      title={`${labels[key] || "Local"} happenings`}
      intro="Hand-picked things to do around Whitefield."
      items={items.filter((x) => x.category === key)}
    />
  );
}
function Weekend({ items }) {
  const weekend = filterItems(
    items.filter((x) => x.status !== "CANCELLED"),
    "This Weekend",
  );
  const groups = [
    ["Top picks", weekend.filter((x) => x.featured)],
    ["Free", weekend.filter((x) => x.is_free)],
    ["Kids & family", weekend.filter((x) => x.category === "KIDS")],
    ["Food & drink", weekend.filter((x) => x.category === "FOOD_DRINK")],
    ["Music", weekend.filter((x) => x.category === "MUSIC")],
    [
      "Workshops & experiences",
      weekend.filter((x) => ["WORKSHOP", "EXPERIENCE"].includes(x.category)),
    ],
  ];
  return (
    <section className="page">
      <Back />
      <div className="page-title">
        <p className="eyebrow">Saturday + Sunday</p>
        <h1>This weekend in Whitefield</h1>
        <p>A quick shortlist for making plans nearby.</p>
      </div>
      {groups
        .filter(([, xs]) => xs.length)
        .map(([name, xs]) => (
          <section className="group" key={name}>
            <h2>{name}</h2>
            <div className="grid">
              {xs.map((x) => (
                <Card key={x.id} item={x} />
              ))}
            </div>
          </section>
        ))}
    </section>
  );
}

function Detail({ items }) {
  const { id } = useParams(),
    item = items.find((x) => x.id === id);
  useEffect(() => {
    if (item) {
      window.scrollTo({ top: 0, behavior: "instant" });
      track("listing_view", { id, detail: true });
      document.title = `${item.title} | Anvesho`;
    }
    return () => {
      document.title = "Anvesho | What's happening in Whitefield?";
    };
  }, [item, id]);
  if (!item) return <NotFound />;
  const url = item.booking_url || item.source_url;
  const nearby = items.filter((other) => other.id !== item.id && other.status !== "CANCELLED").slice(0, 3);
  return (
    <section className="page happening-page">
      <Back />
      <header className="happening-heading">
        <p className="eyebrow">{labels[item.category]} · {item.locality || "Whitefield"}</p>
        <h1>{item.title}</h1>
        <p>{item.description}</p>
      </header>
      <ListingImage key={listingImage(item)} className="detail-lead-image" src={listingImage(item)} alt={item.title} position={listingImagePosition(item)} eager />
      {item.is_demo && <p className="image-credit">Sample listing · Illustrative imagery</p>}
      <div className="detail">
        <article>
          <div className="tags">
            <span>{labels[item.category]}</span>
            {item.is_demo && <b>Sample listing</b>}
            {item.status === "CANCELLED" && <em>Cancelled</em>}
          </div>
          <h2>Make a little time for this.</h2>
          <p className="description">{item.venue ? `At ${item.venue}, ${item.locality || "Whitefield"}.` : "Around Whitefield."} {item.is_free ? "Entry is free." : item.price_text || "Check the source for ticket information."}</p>
          {item.editorial_note && <p className="detail-note">“{item.editorial_note}”</p>}
          <div className="facts">
            <Fact
              icon={<CalendarDays />}
              label="Date"
              value={prettyDate(item.start_date)}
            />
            <Fact icon={<Clock />} label="Starts" value={item.start_time} />
            <Fact
              icon={<MapPin />}
              label="Venue"
              value={`${item.venue}, ${item.locality}`}
            />
            <Fact icon={<Ticket />} label="Price" value={item.price_text} />
          </div>
        </article>
        <aside>
          <h2>Plan your visit</h2>
          <p>Last verified {verified(item.verified_at)}</p>
          {url && item.status !== "CANCELLED" ? <a
            className="primary"
            href={url}
            target="_blank"
            rel="noreferrer"
            onClick={() =>
              track(item.booking_url ? "booking_click" : "source_click", { id })
            }
          >
            {item.booking_url ? "View booking" : "View source"} <ExternalLink />
          </a> : <p>{item.status === "CANCELLED" ? "This happening has been cancelled." : "Booking information is not available yet."}</p>}
          <small>Source: {item.source_name}</small>
          {item.venue && <a className="directions-link" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${item.venue}, ${item.locality || "Whitefield"}, Bengaluru`)}`} target="_blank" rel="noreferrer"><MapPin size={16} /> Get directions <ExternalLink size={14} /></a>}
          <a
            className="report"
            href={`mailto:hello@anvesho.local?subject=Listing issue: ${item.title}`}
          >
            Something wrong with this listing?
          </a>
        </aside>
      </div>
      {nearby.length > 0 && <section className="more-nearby"><div className="nearby-heading"><div><p className="eyebrow">Keep exploring</p><h2>More around the neighbourhood</h2></div><Link to="/">All happenings →</Link></div><div className="grid">{nearby.map((other) => <Card key={other.id} item={other} />)}</div></section>}
    </section>
  );
}
function Fact({ icon, label, value }) {
  return (
    <div>
      {icon}
      <span>
        <small>{label}</small>
        <strong>{value}</strong>
      </span>
    </div>
  );
}
function Back() {
  return (
    <Link className="back" to="/">
      <ChevronLeft />
      All happenings
    </Link>
  );
}

const blank = {
  title: "",
  description: "",
  category: "EVENT",
  start_date: dateFromNow(1),
  end_date: dateFromNow(1),
  start_time: "18:00",
  venue: "",
  locality: "Whitefield",
  price_text: "",
  is_free: false,
  source_name: "",
  source_url: "",
  booking_url: "",
  image_url: "",
  featured: false,
  status: "UPCOMING",
};
function Form({ value, onSubmit, label = "Save happening" }) {
  const [form, setForm] = useState(value || blank);
  useEffect(() => setForm(value || blank), [value]);
  const set = (key, val) => setForm((x) => ({ ...x, [key]: val }));
  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(form);
      }}
    >
      <Field full label="Name">
        <input
          required
          value={form.title}
          onChange={(e) => set("title", e.target.value)}
        />
      </Field>
      <Field label="Category">
        <select
          value={form.category}
          onChange={(e) => set("category", e.target.value)}
        >
          {CATEGORIES.map((x) => (
            <option key={x} value={x}>
              {labels[x]}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Date">
        <input
          type="date"
          required
          value={form.start_date}
          onChange={(e) => {
            set("start_date", e.target.value);
            set("end_date", e.target.value);
          }}
        />
      </Field>
      <Field label="Time">
        <input
          type="time"
          required
          value={form.start_time}
          onChange={(e) => set("start_time", e.target.value)}
        />
      </Field>
      <Field label="Venue">
        <input
          required
          value={form.venue}
          onChange={(e) => set("venue", e.target.value)}
        />
      </Field>
      <Field label="Locality">
        <input
          required
          value={form.locality}
          onChange={(e) => set("locality", e.target.value)}
        />
      </Field>
      <Field label="Price">
        <input
          value={form.price_text}
          onChange={(e) => set("price_text", e.target.value)}
          placeholder="Free or Rs 500"
        />
      </Field>
      <Field full label="Description">
        <textarea
          required
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
        />
      </Field>
      <Field full label="Website / Instagram / booking URL">
        <input
          type="url"
          required
          value={form.source_url}
          onChange={(e) => set("source_url", e.target.value)}
          placeholder="https://"
        />
      </Field>
      <Field label="Organizer / source">
        <input
          required
          value={form.source_name}
          onChange={(e) => set("source_name", e.target.value)}
        />
      </Field>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={form.is_free}
          onChange={(e) => {
            set("is_free", e.target.checked);
            if (e.target.checked) set("price_text", "Free");
          }}
        />
        Free entry
      </label>
      <button className="primary full" type="submit">
        <Check />
        {label}
      </button>
    </form>
  );
}
function Field({ label, full, children }) {
  return (
    <label className={full ? "full" : ""}>
      {label}
      {children}
    </label>
  );
}
function Submit() {
  const [done, setDone] = useState(false);
  const [submission, setSubmission] = useState({
    source_url: "",
    title: "",
    start_date: "",
    start_time: "",
    venue: "",
    image_url: "",
    notes: "",
    contact: "",
  });
  useEffect(() => track("submit_started"), []);
  const set = (key, value) =>
    setSubmission((current) => ({ ...current, [key]: value }));
  const submit = (event) => {
    event.preventDefault();
    const list = read(SUBMIT_KEY, []);
    list.push({
      ...submission,
      id: `submission-${Date.now()}`,
      description: submission.notes,
      category: "OTHER",
      source_name: "Community submission",
      created_at: now(),
      verified_at: null,
      featured: false,
      status: "PENDING",
    });
    localStorage.setItem(SUBMIT_KEY, JSON.stringify(list));
    track("submit_completed");
    setDone(true);
  };
  return (
    <section className="narrow">
      <Back />
      {done ? (
        <div className="success">
          <i>
            <Check />
          </i>
          <h1>Thanks for the tip</h1>
          <p>Your submission is saved locally and waiting for review.</p>
          <Link className="primary" to="/">
            Browse happenings
          </Link>
        </div>
      ) : (
        <>
          <div className="page-title">
            <p className="eyebrow">The neighbourhood tip desk</p>
            <h1>Submit a happening</h1>
            <p>
              Share an event, opening, pop-up, workshop, food experience or
              anything noteworthy around Whitefield.
            </p>
          </div>
          <form className="link-submit" onSubmit={submit}>
            <div className="link-primary">
              <label htmlFor="submission-url">
                Paste an Instagram/event/website link
              </label>
              <p>We will use the link to collect the public listing details.</p>
              <input
                id="submission-url"
                type="url"
                required
                autoFocus
                placeholder="https://instagram.com/p/... or event website"
                value={submission.source_url}
                onChange={(event) => set("source_url", event.target.value)}
              />
            </div>
            <details>
              <summary>Add optional details</summary>
              <div className="optional-fields">
                <Field label="Title">
                  <input
                    value={submission.title}
                    onChange={(event) => set("title", event.target.value)}
                  />
                </Field>
                <Field label="Date">
                  <input
                    type="date"
                    value={submission.start_date}
                    onChange={(event) => set("start_date", event.target.value)}
                  />
                </Field>
                <Field label="Time">
                  <input
                    type="time"
                    value={submission.start_time}
                    onChange={(event) => set("start_time", event.target.value)}
                  />
                </Field>
                <Field label="Venue">
                  <input
                    value={submission.venue}
                    onChange={(event) => set("venue", event.target.value)}
                  />
                </Field>
                <Field full label="Image or poster URL">
                  <input
                    type="url"
                    placeholder="https://"
                    value={submission.image_url}
                    onChange={(event) => set("image_url", event.target.value)}
                  />
                </Field>
                <Field full label="Notes">
                  <textarea
                    placeholder="Anything useful that is not clear from the link"
                    value={submission.notes}
                    onChange={(event) => set("notes", event.target.value)}
                  />
                </Field>
                <Field full label="Contact details">
                  <input
                    placeholder="Email or phone (optional)"
                    value={submission.contact}
                    onChange={(event) => set("contact", event.target.value)}
                  />
                </Field>
              </div>
            </details>
            <p className="pending-note">
              Every submission is reviewed before it appears on Anvesho.
            </p>
            <button className="primary" type="submit">
              Submit for review
            </button>
          </form>
        </>
      )}
    </section>
  );
}

function Admin({ items, setItems }) {
  const [editing, setEditing] = useState(null),
    [show, setShow] = useState(false),
    submissions = read(SUBMIT_KEY, []);
  const update = (id, values) =>
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, ...values } : x)));
  const save = (form) => {
    const item = {
      ...form,
      id: editing?.id || `happening-${Date.now()}`,
      created_at: editing?.created_at || now(),
      verified_at: editing?.verified_at || now(),
      is_demo: editing?.is_demo || false,
    };
    setItems((xs) =>
      editing ? xs.map((x) => (x.id === editing.id ? item : x)) : [item, ...xs],
    );
    setEditing(null);
    setShow(false);
  };
  return (
    <section className="admin page">
      <div className="admin-head">
        <div>
          <p className="eyebrow">The newsroom desk</p>
          <h1>Manage the issue</h1>
          <p>Stored in this browser only. No authentication is enabled.</p>
        </div>
        <button
          className="primary"
          onClick={() => {
            setEditing(null);
            setShow(!show);
          }}
        >
          <Plus />
          Add happening
        </button>
      </div>
      {show && (
        <div className="admin-form">
          <h2>{editing ? "Edit" : "Add"} happening</h2>
          <Form value={editing} onSubmit={save} />
        </div>
      )}
      <div className="summary">
        <span>
          <b>{items.length}</b> published
        </span>
        <span>
          <b>{submissions.length}</b> pending submissions
        </span>
      </div>
      <div className="admin-list">
        {items.map((item) => (
          <article key={item.id}>
            <div>
              <span className="tag">{labels[item.category]}</span>
              <h3>{item.title}</h3>
              <p>
                {prettyDate(item.start_date)} at {item.start_time} ·{" "}
                {item.venue}
              </p>
            </div>
            <div className="actions">
              <button
                onClick={() => update(item.id, { featured: !item.featured })}
              >
                {item.featured ? "Unfeature" : "Feature"}
              </button>
              <button
                onClick={() =>
                  update(item.id, {
                    status:
                      item.status === "CANCELLED" ? "UPCOMING" : "CANCELLED",
                  })
                }
              >
                {item.status === "CANCELLED" ? "Restore" : "Cancel"}
              </button>
              <button onClick={() => update(item.id, { verified_at: now() })}>
                Verify now
              </button>
              <button
                onClick={() => {
                  setEditing(item);
                  setShow(true);
                  window.scrollTo(0, 0);
                }}
              >
                Edit
              </button>
              <button
                aria-label={`Delete ${item.title}`}
                onClick={() =>
                  window.confirm(`Delete ${item.title}?`) &&
                  setItems((xs) => xs.filter((x) => x.id !== item.id))
                }
              >
                <Trash2 />
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
const Empty = React.forwardRef(function Empty(props, ref) {
  return (
    <div className="empty" ref={ref} {...props}>
      <img className="empty-mascot" src={parakeetBlink} alt="" aria-hidden="true" />
      <h3>No matching happenings yet</h3>
      <p>Try another filter or check back after the next curation update.</p>
    </div>
  );
});
function NotFound() {
  return (
    <div className="success">
      <h1>That page wandered off</h1>
      <p>The listing may have moved or ended.</p>
      <Link className="primary" to="/">
        See what's happening
      </Link>
    </div>
  );
}
